import { useMemo, useCallback, useState } from 'react';
import type { Review, ReviewType } from '@trackmind/core';
import { buildReview, reviewPeriod, findReview } from '@trackmind/core';
import { useApp } from '@/context/AppContext'
import { useToday } from '@/hooks/useToday';

/**
 * Reviews, and the notes written on them.
 *
 * These used to live in this hook's own `useState` and nowhere else — no
 * IndexedDB row, no sync file — so every reflection a user typed was gone the
 * moment the page reloaded or they navigated away and back. The generated
 * content was recomputed each time too, which meant a review of March opened
 * in June reported different numbers if any of that work had since been
 * edited or deleted; a review is supposed to be a snapshot of a period, not a
 * live query over it.
 *
 * Both are fixed by persisting through AppContext. Review ids stay
 * deterministic (`review-weekly-<Monday>`, from core's reviewPeriod), so the same period generated
 * independently on two devices produces one id and the sync merge reconciles
 * it by `updated_at` instead of accumulating duplicates.
 */
export function useReviews(reviewType?: ReviewType) {
  const {
    tasks, schedules, goals, lists, projects, habitInstances,
    reviews, saveReview, deleteReview: removeReview, isInitialized, storage,
    fitnessWorkouts, fitnessSettings, preferences,
  } = useApp();
  const todayKey = useToday()
  const [currentReview, setCurrentReview] = useState<Review | null>(null);
  /** Set when generation threw. Distinguishes "it broke" from "nothing yet". */
  const [generationError, setGenerationError] = useState<string | null>(null);

  // Filter reviews by type if specified
  const filteredReviews = useMemo(() => {
    if (!reviewType) return reviews;
    return reviews.filter(r => r.review_type === reviewType);
  }, [reviews, reviewType]);

  /**
   * Write a freshly generated review WITHOUT discarding what the user wrote.
   *
   * Review ids are deterministic (`review-daily-2026-08-12`), which is what
   * lets two devices generate the same period independently and have the sync
   * merge reconcile them rather than pile up duplicates. The cost is that a
   * regeneration targets the same row — so saving a newly generated review
   * over one that already carries notes would silently delete them, which is
   * the exact bug persistence was added to fix.
   *
   * `isInitialized` matters for the same reason: reviews arrive from storage
   * asynchronously, and generating against an empty in-memory list before
   * that lands would find no prior review to carry forward from.
   */
  const persistGenerated = useCallback(async (generated: Review): Promise<Review> => {
    const prior = reviews.find(r => r.id === generated.id);
    const merged: Review = prior
      ? { ...generated, notes: prior.notes, created_at: prior.created_at, updated_at: prior.updated_at }
      : generated;
    // Belt and braces on top of the in-memory lookup: the context loads
    // reviews before isInitialized turns true, but a caller created in the
    // same render pass can still hold a stale empty `reviews` in its closure.
    // Since ids are deterministic, writing through that closure would replace
    // the stored row — so when no prior is in memory, check storage itself
    // before the first write, and never pave over notes with emptiness.
    if (!isInitialized) return merged;
    if (!prior) {
      try {
        const onDisk = (await storage?.getReviews())?.find(r => r.id === generated.id);
        if (onDisk?.notes) {
          const kept: Review = {
            ...generated,
            notes: onDisk.notes,
            created_at: onDisk.created_at,
            updated_at: onDisk.updated_at,
          };
          await saveReview(kept);
          return kept;
        }
      } catch { /* storage unavailable — fall through to the plain save */ }
    }
    await saveReview(merged);
    return merged;
  }, [reviews, saveReview, isInitialized, storage]);

  /**
   * A review of `type` for the period containing `day`, from core's
   * `buildReview` — the builder the assistant uses too, so the same period
   * gets the same id, date, window and numbers from either door.
   */
  const buildFor = useCallback((type: ReviewType, day: string): Review | null => {
    try {
      return buildReview(type, day, {
        tasks,
        goals: goals ?? [],
        lists: lists ?? [],
        schedules: Array.from(schedules.values()),
        // Lets goal progress include linked-project completion
        projects: projects ?? [],
        // Habit adherence and the habit part of goal progress both come from
        // completion records — a habit task's status is never 'completed'.
        habitInstances: habitInstances ?? [],
        // Workouts in the period, if any — the review gains a gym line.
        fitnessWorkouts,
        fitnessUnit: fitnessSettings.unit,
        // Which deadlines are "upcoming" — today and zone as the rest of the app reads them.
        today: todayKey(),
        timezone: preferences?.timezone,
      });
    } catch (error) {
      // A thrown review must not take the page down — but it must not look
      // like a real week of zeros either. Returning createDefaultReviewContent()
      // here once made a THROW render as 0 tasks, 0% habits, 0/100,
      // pixel-identical to an empty week, which is why "the weekly review is
      // broken" came back four times. A failure now has to say it failed.
      console.error('[useReviews] Review generation failed:', error);
      setGenerationError(error instanceof Error ? error.message : String(error));
      return null;
    }
  }, [tasks, schedules, goals, lists, projects, habitInstances, fitnessWorkouts, fitnessSettings.unit, todayKey, preferences?.timezone]);

  /**
   * Return a stored review — refreshed if its period is still running.
   *
   * Persistence made reviews snapshots, which is right for a period that has
   * ENDED: March's review should keep saying what March said. But the same
   * freeze applied to the current period meant the sidebar's weekly widget
   * showed Monday-morning zeros all week — the numbers were locked in at
   * whatever instant the review happened to be first generated.
   *
   * So a review whose period_end is today or later gets its content
   * recomputed on read. Notes, created_at and updated_at are kept: updated_at
   * is the sync merge's tiebreaker for the user's own edits, and bumping it
   * for an automatic refresh would make merely *looking* at the widget win
   * merges against real note edits from another device. The write is skipped
   * entirely when the recomputed content is identical, so viewing doesn't
   * churn a sync every render.
   */
  const freshened = useCallback(async (existing: Review): Promise<Review> => {
    const today = todayKey();
    if (existing.period_end < today) return existing; // closed period: frozen
    const content = buildFor(existing.review_type, existing.period_start)?.content;
    // A failed refresh keeps what's stored — stale beats blank.
    if (!content) return existing;
    if (JSON.stringify(content) === JSON.stringify(existing.content)) return existing;
    const refreshed: Review = { ...existing, content };
    if (isInitialized) await saveReview(refreshed);
    return refreshed;
  }, [isInitialized, saveReview, buildFor, todayKey]);

  /** The stored review for the period containing `day`, or a new one. */
  const reviewFor = useCallback(async (type: ReviewType, day: string): Promise<Review> => {
    const period = reviewPeriod(type, day) ?? reviewPeriod(type, todayKey());
    if (!period) throw new Error(`Could not work out the ${type} period for ${day}`);
    const existing = findReview(reviews, period);
    if (existing) {
      const fresh = await freshened(existing);
      setCurrentReview(fresh);
      return fresh;
    }
    const built = buildFor(type, period.start);
    // Never persist a failed generation: a zeroed review written to storage
    // would then be read back as though it were real.
    if (!built) throw new Error(`Could not build the ${type} review`);
    const saved = await persistGenerated({ ...built, notes: '', created_at: new Date().toISOString() });
    setCurrentReview(saved);
    return saved;
  }, [reviews, buildFor, persistGenerated, freshened, todayKey]);

  const getDaily = useCallback((date?: string) => reviewFor('daily', date ?? todayKey()), [reviewFor, todayKey]);
  const getWeekly = useCallback((date?: string) => reviewFor('weekly', date ?? todayKey()), [reviewFor, todayKey]);
  const getMonthly = useCallback((date?: string) => reviewFor('monthly', date ?? todayKey()), [reviewFor, todayKey]);

  const getReview = useCallback(async (type: ReviewType, periodKey: string): Promise<Review | null> => {
    const existing = reviews.find(r => r.review_type === type && r.date === periodKey);
    if (existing) {
      setCurrentReview(existing);
      return existing;
    }
    return null;
  }, [reviews]);

  const updateReviewNotes = useCallback(async (type: ReviewType, periodKey: string, notes: string): Promise<Review> => {
    const existing = reviews.find(r => r.review_type === type && r.date === periodKey);
    if (!existing) {
      throw new Error(`Review not found: ${type} ${periodKey}`);
    }

    const updated: Review = {
      ...existing,
      notes,
      // Moves on every edit, which is what the sync merge resolves on. Without
      // it, two devices editing the same period's notes would resolve by
      // whichever happened to be written last rather than by which is newer.
      updated_at: new Date().toISOString(),
    };

    await saveReview(updated);
    if (currentReview?.id === updated.id) {
      setCurrentReview(updated);
    }
    return updated;
  }, [reviews, currentReview, saveReview]);

  const deleteReview = useCallback(async (type: ReviewType, periodKey: string): Promise<void> => {
    const existing = reviews.find(r => r.review_type === type && r.date === periodKey);
    if (existing) await removeReview(existing.id);
    if (currentReview?.review_type === type && currentReview?.date === periodKey) {
      setCurrentReview(null);
    }
  }, [reviews, currentReview, removeReview]);

  // Filter by type
  const dailyReviews = useMemo(() => filteredReviews.filter(r => r.review_type === 'daily'), [filteredReviews]);
  const weeklyReviews = useMemo(() => filteredReviews.filter(r => r.review_type === 'weekly'), [filteredReviews]);
  const monthlyReviews = useMemo(() => filteredReviews.filter(r => r.review_type === 'monthly'), [filteredReviews]);

  /**
   * The displayed review, tracked against storage rather than frozen.
   *
   * `currentReview` is set from whatever object the generate path returned,
   * which is a snapshot taken at that instant. Reviews load from IndexedDB
   * asynchronously, so on a fresh page load the view could be holding a
   * just-generated copy with empty notes while the stored row two hundred
   * milliseconds later has the text the user actually wrote. Re-reading it by
   * id each render means the note appears as soon as storage lands, and any
   * edit arriving from a sync pull shows up without a manual refresh.
   */
  const liveCurrentReview = useMemo(
    () => (currentReview ? reviews.find(r => r.id === currentReview.id) ?? currentReview : null),
    [reviews, currentReview],
  );

  return {
    reviews: filteredReviews,
    dailyReviews,
    weeklyReviews,
    monthlyReviews,
    currentReview: liveCurrentReview,
    loading: false,
    error: generationError,
    refetch: () => Promise.resolve(),
    getDaily,
    getWeekly,
    getMonthly,
    getReview,
    updateReviewNotes,
    deleteReview,
    setCurrentReview,
  };
}
